import React, { HTMLAttributes, PropsWithChildren } from 'react'

import classNames from 'classnames'

import { getValidProps } from 'hsds/utils/react'

import { HeadingUI } from './Heading.styles'

export const SIZES = [
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'xl',
  'big',
  'md',
  'sm',
  'small',
] as const

type Props = {
  /** Center aligns text. */
  center?: boolean
  /** Custom class names to be added to the component. */
  className?: string
  /** Disables text selection. */
  disableSelect?: boolean
  /** Lightens the heading color. */
  light?: boolean
  /** Inherit the line-height from a parent selector. */
  lineHeightInherit?: boolean
  /** Resets the line-height to `1`. */
  lineHeightReset?: boolean
  /** Applies `Link` styles. */
  linkStyle?: boolean
  /** Prevents text from wrapping. */
  noWrap?: boolean
  /** Sets HTML element. */
  selector?: string
  /** Adjust heading size. */
  size?: (typeof SIZES)[number]
  /** Enables CSS truncation for text. */
  truncate?: boolean
  /** Adjust text weight. */
  weight?: number | string
  /** Enables CSS `word-break` for text. */
  wordWrap?: boolean
  /** Data attr for Cypress tests. */
  'data-cy'?: string
} & HTMLAttributes<HTMLElement>

const Heading = (props: PropsWithChildren<Props>) => {
  const {
    center,
    children,
    className,
    disableSelect,
    light,
    lineHeightInherit,
    lineHeightReset,
    linkStyle,
    noWrap,
    selector,
    size,
    truncate,
    weight,
    wordWrap,
    ...rest
  } = props

  const selectorTag = selector || 'div'

  return (
    <HeadingUI
      as={selectorTag}
      {...getValidProps(rest)}
      className={classNames(
        'c-Heading',
        center && 'is-center',
        disableSelect && 'is-disableSelect',
        light && 'is-light',
        lineHeightInherit && 'is-lineHeightInherit',
        lineHeightReset && 'is-lineHeightReset',
        linkStyle && 'is-linkStyle',
        size && `is-${size}`,
        truncate && 'is-truncate',
        weight && `is-${weight}`,
        noWrap && 'is-noWrap',
        wordWrap && 'is-wordWrap',
        className
      )}
    >
      {children}
    </HeadingUI>
  )
}

Heading.defaultProps = {
  center: false,
  'data-cy': 'Heading',
  disableSelect: false,
  lineHeightInherit: false,
  lineHeightReset: false,
  linkStyle: false,
  truncate: false,
  noWrap: false,
  wordWrap: false,
}

export default Heading
